import { useState, useEffect, useRef, useId } from 'react';

interface NominatimResult {
  place_id: number;
  display_name: string;
  address: {
    road?: string;
    house_number?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    suburb?: string;
    state?: string;
    country?: string;
    country_code?: string;
  };
}

export interface AddressResult {
  fullAddress: string;
  street?: string;
  houseNumber?: string;
  postcode?: string;
  city?: string;
  state?: string;
}

interface AddressAutocompleteProps {
  // Kontrolliert vom Elternteil, damit das Formular den Rohtext auch ohne
  // ausgewählten Vorschlag absenden kann
  value: string;
  onChange: (value: string) => void;
  onSelect: (address: AddressResult) => void;
  placeholder?: string;
  disabled?: boolean;
}

const HINWEIS_SUCHE_NICHT_VERFUEGBAR =
  'Die Adresssuche ist gerade nicht verfügbar. Sie können Ihre Adresse trotzdem eintippen und absenden.';
const HINWEIS_KEIN_VORSCHLAG =
  'Kein passender Vorschlag gefunden. Sie können Ihre Adresse trotzdem so absenden.';

export function AddressAutocomplete({ value, onChange, onSelect, placeholder, disabled }: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hint, setHint] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const abortRef = useRef<AbortController>();
  const cacheRef = useRef<Map<string, NominatimResult[]>>(new Map());
  const listboxId = useId();
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  useEffect(() => {
    if (value.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setHint(null);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      // Cache-Hit → sofort anzeigen
      if (cacheRef.current.has(value)) {
        const cached = cacheRef.current.get(value)!;
        setSuggestions(cached);
        setShowSuggestions(cached.length > 0);
        setSelectedIndex(-1);
        setHint(cached.length > 0 ? null : HINWEIS_KEIN_VORSCHLAG);
        return;
      }

      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;

      setIsLoading(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&format=json&addressdetails=1&limit=5&countrycodes=de&accept-language=de`,
          {
            signal: controller.signal,
            headers: { 'User-Agent': 'SinanWerbeanlage/1.0' },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data: NominatimResult[] = await response.json();

        // Nur die Antwort zur aktuellen Eingabe darf die Liste setzen
        if (controller.signal.aborted) return;

        cacheRef.current.set(value, data);
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
        setSelectedIndex(-1);
        setHint(data.length > 0 ? null : HINWEIS_KEIN_VORSCHLAG);
      } catch (error) {
        if (controller.signal.aborted) return;
        setSuggestions([]);
        setShowSuggestions(false);
        setHint(HINWEIS_SUCHE_NICHT_VERFUEGBAR);
      } finally {
        // Eine abgebrochene Anfrage darf den Ladezustand einer neueren nicht beenden
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, 400);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      // Sobald sich die Eingabe ändert oder die Komponente verschwindet, ist eine
      // laufende Anfrage veraltet. Sonst überschreibt ihre späte Antwort die Liste,
      // die zur aktuellen Eingabe gehört, und der Nutzer klickt eine falsche Adresse an.
      abortRef.current?.abort();
      setIsLoading(false);
    };
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatAddress = (result: NominatimResult): string => {
    const { road, house_number, postcode, city, town, village } = result.address;
    const parts: string[] = [];
    const ort = city || town || village;

    if (road) {
      parts.push(house_number ? `${road} ${house_number}` : road);
    }

    if (postcode && ort) {
      parts.push(`${postcode} ${ort}`);
    } else if (ort) {
      parts.push(ort);
    }

    return parts.length > 0 ? parts.join(', ') : result.display_name.split(',').slice(0, 3).join(',');
  };

  const handleSelect = (result: NominatimResult) => {
    const { road, house_number, postcode, city, town, village, state } = result.address;

    const addressResult: AddressResult = {
      fullAddress: formatAddress(result),
      street: road,
      houseNumber: house_number,
      postcode,
      city: city || town || village,
      state,
    };

    setShowSuggestions(false);
    onChange(addressResult.fullAddress);
    onSelect(addressResult);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        // Ohne markierten Vorschlag läuft Enter ins Formular und sendet den Freitext
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  const isOpen = showSuggestions && suggestions.length > 0;
  const statusText = isOpen
    ? `${suggestions.length} ${suggestions.length === 1 ? 'Adressvorschlag' : 'Adressvorschläge'}, mit den Pfeiltasten auswählen`
    : hint || '';

  return (
    <div className="address-autocomplete">
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label="Adresse der Werbeanlage"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={isOpen && selectedIndex >= 0 ? optionId(selectedIndex) : undefined}
        className="chat-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Adresse eingeben...'}
        disabled={disabled}
        autoComplete="off"
      />

      {isLoading && (
        <div className="autocomplete-loading" aria-hidden="true">
          <span className="loading-dot"></span>
          <span className="loading-dot"></span>
          <span className="loading-dot"></span>
        </div>
      )}

      <div className="sr-only" role="status">{statusText}</div>

      {hint && !isOpen && (
        <div className="autocomplete-hint">{hint}</div>
      )}

      {isOpen && (
        <div
          ref={suggestionsRef}
          id={listboxId}
          role="listbox"
          aria-label="Adressvorschläge"
          className="autocomplete-suggestions"
        >
          {suggestions.map((result, index) => (
            <button
              key={result.place_id}
              id={optionId(index)}
              type="button"
              role="option"
              aria-selected={index === selectedIndex}
              tabIndex={-1}
              className={`autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="autocomplete-icon" aria-hidden="true">📍</span>
              <span className="autocomplete-text">{formatAddress(result)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
